import * as R from "remeda";
import { ActionIcon, Box, Button, Group, Progress, Text, Tooltip } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { IconClockHour3, IconEdit, IconTrash } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import { DataTable } from "mantine-datatable"
import { AddCreditKeyDialog } from "./AddCreditKeyDialog";
import { useState } from "react";
import { UpdateCreditKeyDialog } from "./UpdateCreditKeyDialog";
import { useDeleteApiAdminChatCreditKeyDelete, useGetApiAdminChatCreditKeyGetValidCreditByUserId, useGetApiAdminChatCreditKeyListByUserId } from "@/API";
import { TimestampToDate } from "@/Helpers/DateHelper";

export function ChatCreditKeyList({ userId }: { userId: string }) {
    const queryClient = useQueryClient()
    const [createKey_opened, { open: createKey_open, close: createKey_close }] = useDisclosure(false);
    const [editKey_opened, { open: editKey_open, close: editKey_close }] = useDisclosure(false);
    const [selectedKeyId, setSelectedKeyId] = useState("")
    const list = useGetApiAdminChatCreditKeyListByUserId({ userId: userId })
    const deleteKey_Mutation = useDeleteApiAdminChatCreditKeyDelete()
    const validCredit = useGetApiAdminChatCreditKeyGetValidCreditByUserId({ userId: userId })
    return <>
        <Button w={150} onClick={() => createKey_open()}>
            新增儲值金鑰
        </Button>
        <Text>
            剩餘有效點數 : {validCredit.data?.data}
        </Text>
        {list.isSuccess && <>
            <DataTable
                minHeight={(list.data.data.results?.length ?? 0) > 0 ? 0 : 150}
                noRecordsText="沒有儲值金鑰"
                withRowBorders={false}
                columns={[
                    { accessor: "id", width: 300 },
                    { accessor: "startTimestamp", width: 200, title: "開始時間", render: (data) => (TimestampToDate(data.startTimestamp ?? 0)).toLocaleString() },
                    { accessor: "endTimestamp", width: 200, title: "到期時間", render: (data) => (TimestampToDate(data.endTimestamp ?? 0)).toLocaleString() },
                    {
                        accessor: "credit", width: 150, title: "聊天點數", render: (data) => <>
                            <Tooltip label={`${data.credit}/${data.totalCredit}`}>
                                <Progress.Root size="xl">
                                    <Progress.Section value={(data.credit ?? 0) / (data.totalCredit ?? 1) * 100}>
                                        <Progress.Label>{data.credit}/{data.totalCredit}</Progress.Label>
                                    </Progress.Section>
                                </Progress.Root></Tooltip>
                        </>
                    },
                    {
                        accessor: "description", title: "註記", render: (data) => <Tooltip style={{ whiteSpace: "pre-wrap" }} label={data.description} multiline><Text variant="text" w={100} fw={300} truncate="end">{data.description}</Text></Tooltip>
                    },
                    {
                        accessor: 'actions',
                        title: <Box mr={6}>操作</Box>,
                        textAlign: 'right',
                        render: (data, idx) => (
                            <Group gap={4} justify="right" wrap="nowrap">
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={() => {
                                        if (data.id) {
                                            setSelectedKeyId(data.id)
                                            editKey_open()
                                        }
                                    }}
                                >
                                    <IconEdit size={16} />
                                </ActionIcon>
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => {
                                        modals.openContextModal({
                                            modal: "ConfirmCountDown",
                                            title: '刪除儲值金鑰',
                                            innerProps: {
                                                countDownSecond: 5,
                                                children: (
                                                    <Text size="sm">
                                                        是否要刪除儲值金鑰?
                                                    </Text>
                                                ),
                                                labels: { confirm: '刪除', cancel: '取消' },
                                                confirmProps: { color: 'red' },
                                                onConfirm: async () => {
                                                    try {
                                                        if (data.id) {
                                                            await deleteKey_Mutation.mutateAsync({ params: { id: data.id } })
                                                            queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                            notifications.show({ message: "刪除成功" })
                                                        }
                                                    } catch (e) {
                                                        notifications.show({ message: "刪除失敗", color: "red" })
                                                    }
                                                }
                                            },
                                        });
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Group>
                        ),
                    }
                ]}
                records={list.data.data.results}
            />
        </>
        }
        <AddCreditKeyDialog userId={userId} opened={createKey_opened} onClose={() => createKey_close()} />
        {selectedKeyId && <UpdateCreditKeyDialog id={selectedKeyId} opened={editKey_opened} onClose={() => editKey_close()} />}
    </>
}